import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Banner, BannerAnimation } from '@chicare/shared';

import { Icon } from '@/components/icons/Icon';
import { useSectorLink } from '@/lib/sector';
import { useReducedMotion } from '@/lib/useReducedMotion';
import { trackBanners } from '@/lib/bannerTracking';

/**
 * The shop front carousels.
 *
 * ── WHY A HAND-ROLLED ONE ───────────────────────────────────────────────────
 *
 * Because the hard parts of a carousel are not the sliding. They are: stopping
 * when somebody is reading, not fighting a thumb mid-drag, not animating for
 * people who asked for no animation, and not turning in a tab nobody is
 * looking at. A library gives the sliding and leaves those to the caller
 * anyway, at the cost of 30KB on a connection where every kilobyte is a buyer
 * staring at a grey box.
 *
 * ── WHAT MAKES IT STOP ──────────────────────────────────────────────────────
 *
 * Autoplay pauses on hover, on keyboard focus anywhere inside, while a drag is
 * in progress, and while the tab is hidden. All four are the same rule: a slide
 * that moves away from somebody who was reaching for it is worse than a slide
 * that never moved, because they learn not to reach.
 *
 * ── REDUCED MOTION IS NOT A DEGRADED MODE ───────────────────────────────────
 *
 * For somebody with vestibular migraine a moving shop front is a page they
 * cannot use at all. So `prefers-reduced-motion` drops the animation AND stops
 * the autoplay entirely: the carousel becomes a thing you page through, which
 * is a complete experience rather than a lesser one. It is not configurable
 * from the admin console on purpose — it is the viewer's decision, not the
 * merchandiser's.
 */

interface Props {
  rows: Record<string, Banner[]>;
  rowCount: number;
  intervalSeconds: number;
  animation: BannerAnimation;
  autoplay: boolean;
  /** CSS ratio for the strip from 640px up, e.g. "5/1". Phones stay 16:7. */
  ratio: string;
  className?: string;
}

export function BannerCarousel({
  rows,
  rowCount,
  intervalSeconds,
  animation,
  autoplay,
  ratio,
  className,
}: Props) {
  // Only the rows the admin asked for, and only the ones with something in
  // them. An empty strip of grey where a campaign used to be is worse than no
  // strip at all.
  const strips = useMemo(
    () =>
      Array.from({ length: Math.max(0, rowCount) }, (_, index) => rows[String(index + 1)] ?? [])
        .map((slides, index) => ({ slides, row: index + 1 }))
        .filter((strip) => strip.slides.length > 0),
    [rows, rowCount],
  );

  if (strips.length === 0) {
    return null;
  }

  return (
    <div className={className} data-testid="banner-carousels">
      {strips.map((strip, index) => (
        <BannerRow
          key={strip.row}
          row={strip.row}
          slides={strip.slides}
          animation={animation}
          autoplay={autoplay}
          // The second row is offset by a third of the interval so the two
          // never turn in lockstep. Two strips flipping on the same tick reads
          // as one big flashing thing; staggered, it reads as a shop with
          // activity in it.
          intervalMs={intervalSeconds * 1000 + index * Math.round((intervalSeconds * 1000) / 3)}
          ratio={ratio}
          className={index > 0 ? 'mt-2.5' : undefined}
        />
      ))}
    </div>
  );
}

function BannerRow({
  row,
  slides,
  animation,
  autoplay,
  intervalMs,
  ratio,
  className,
}: {
  row: number;
  slides: Banner[];
  animation: BannerAnimation;
  autoplay: boolean;
  intervalMs: number;
  ratio: string;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [held, setHeld] = useState(false);

  // Which way the last move went, so the animation knows whether to come from
  // the left or the right. A carousel that always slides one way feels wrong
  // the moment somebody presses "previous".
  const [direction, setDirection] = useState<1 | -1>(1);

  const count = slides.length;
  const current = slides[Math.min(index, count - 1)];

  const go = useCallback(
    (next: number, dir: 1 | -1) => {
      setDirection(dir);
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  const next = useCallback(() => go(index + 1, 1), [go, index]);
  const previous = useCallback(() => go(index - 1, -1), [go, index]);

  // ── Autoplay ────────────────────────────────────────────────────────────
  //
  // Not started at all when there is one slide, when the admin turned it off,
  // while the strip is held, or for reduced motion. `document.hidden` is
  // checked on every tick rather than subscribed to, because the interval is
  // already running and one property read is cheaper than a listener.
  useEffect(() => {
    if (!autoplay || reduced || held || count < 2) {
      return;
    }

    const timer = window.setInterval(() => {
      if (!document.hidden) {
        setDirection(1);
        setIndex((i) => (i + 1) % count);
      }
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [autoplay, reduced, held, count, intervalMs]);

  // ── Counting what was seen ──────────────────────────────────────────────
  //
  // Queued, never sent from here. The sender batches and flushes on navigation
  // away, so a turning carousel does not fire a request every few seconds for
  // as long as somebody leaves the tab open.
  useEffect(() => {
    if (current) {
      trackBanners.seen(current.id);
    }
  }, [current]);

  // ── Dragging ────────────────────────────────────────────────────────────
  //
  // Pointer events rather than touch events, so a mouse drag on a desktop
  // works the same way. The threshold is 40px: below that it was a tap on the
  // slide, and treating it as a drag would swallow the link.
  const dragFrom = useRef<number | null>(null);

  const onPointerDown = (event: React.PointerEvent) => {
    dragFrom.current = event.clientX;
    setHeld(true);
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setHeld(false);

    if (from === null || count < 2) {
      return;
    }

    const moved = event.clientX - from;

    if (Math.abs(moved) > 40) {
      // The drag has consumed the gesture; the link underneath must not also
      // fire, or a swipe navigates.
      event.preventDefault();
      if (moved < 0) {
        next();
      } else {
        previous();
      }
    }
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label={`Offers, row ${row}`}
      data-testid={`banner-row-${row}`}
      className={className}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      // Keyboard focus anywhere inside stops the turning. Somebody tabbing
      // through the slide's own link must not have it move out from under them.
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHeld(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          next();
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          previous();
        }
      }}
    >
      <div
        // ────────────────── TWO SHAPES, AND WHY ──────────────────
        //
        // 16:7 on a phone, 5:1 from 640px up.
        //
        // A single ratio cannot serve both. A phone needs HEIGHT: three lines
        // of text and a button, read at arm's length. That same 16:7 on a
        // 1200px desktop is a 525px slab that pushes the shelf off the screen
        // — which is what shipped, and what was reported.
        //
        // These two numbers are not decoration. They are the canvases the
        // console tells an admin to design on (880×385 and 1600×320), and
        // because the artwork matches its box exactly, `cover` crops nothing
        // and stretches nothing. Change a ratio here and every banner ever
        // uploaded is silently cropped, so change the console's stated sizes
        // in the same commit.
        // `bg-paper`: the box somebody looks at before any artwork has
        // arrived is the page's own background, not a slide's tint. See the
        // Slide below for why that matters.
        className="relative isolate aspect-[16/7] overflow-hidden rounded-card bg-paper"
        // The wide ratio is a SETTING, not a constant, so artwork designed at
        // 3:1 is not cropped by a 5:1 strip. Applied as a CSS variable the
        // stylesheet reads from 640px up — Tailwind cannot generate an
        // arbitrary class from a runtime value.
        style={{ ['--banner-ratio' as string]: ratio }}
        data-banner-strip
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          dragFrom.current = null;
          setHeld(false);
        }}
      >
        {slides.map((slide, slideIndex) => (
          <Slide
            key={slide.id}
            slide={slide}
            // The FIRST slide, not the current one. Which slide is on screen
            // changes every few seconds; which one the browser should fetch
            // before anything else is fixed at the moment the page loads.
            first={slideIndex === 0}
            state={
              slideIndex === index ? 'current' : slideIndex === lastIndex(index, count) ? 'leaving' : 'waiting'
            }
            animation={reduced ? 'none' : animation}
            direction={direction}
          />
        ))}

        {/* Arrows on pointer devices only. On a phone they would cover the
            artwork to duplicate a swipe people already know how to do. */}
        {count > 1 ? (
          <>
            <Arrow side="left" onClick={previous} />
            <Arrow side="right" onClick={next} />
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="mt-1.5 flex items-center justify-center gap-1.5">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              // The dot is 6px and the button is 44px. It used to be 24px,
              // with a comment citing §11 — which asks for 44, so the comment
              // was agreeing with a rule the code was breaking by nearly half.
              //
              // `-my-2.5` takes the extra height back out of the layout, so the
              // dot row looks exactly as it did and a thumb now has something
              // to land on. A carousel dot is small, frequently mis-tapped, and
              // sits directly above the shelf — a miss scrolls the page.
              className="-my-2.5 grid h-11 w-11 place-items-center"
              aria-label={`Show offer ${slideIndex + 1} of ${count}`}
              aria-current={slideIndex === index}
              onClick={() => go(slideIndex, slideIndex > index ? 1 : -1)}
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  slideIndex === index ? 'h-1.5 w-4 bg-blue' : 'h-1.5 w-1.5 bg-line'
                }`}
              />
            </button>
          ))}
        </div>
      ) : null}

      {/* What a screen reader is told when the slide changes by itself. The
          artwork and the arrows are invisible to it; this is the whole of the
          carousel's behaviour as far as assistive tech is concerned. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {current ? `${current.headline}. Offer ${index + 1} of ${count}.` : ''}
      </p>
    </section>
  );
}

/**
 * The darkening over a slide's artwork, or nothing.
 *
 * Returned as a gradient string rather than a class, because the three options
 * are three different gradients and naming them in Tailwind would be three
 * arbitrary-value classes nobody could read.
 */
function scrim(overlay: Banner['overlay']): string | null {
  switch (overlay) {
    case 'none':
      return null;
    case 'soft':
      // Enough to keep white text readable over a mid-tone photograph without
      // visibly greying the artwork.
      return 'linear-gradient(90deg, rgba(0,0,0,.34) 0%, rgba(0,0,0,.18) 50%, rgba(0,0,0,0) 100%)';
    case 'auto':
    default:
      return 'linear-gradient(90deg, rgba(0,0,0,.62) 0%, rgba(0,0,0,.42) 46%, rgba(0,0,0,.08) 100%)';
  }
}

/** Which slide is on its way out, so it can animate rather than vanish. */
function lastIndex(index: number, count: number): number {
  return (index - 1 + count) % count;
}

type SlideState = 'current' | 'leaving' | 'waiting';

function Slide({
  slide,
  state,
  animation,
  direction,
  first,
}: {
  slide: Banner;
  state: SlideState;
  animation: BannerAnimation | 'none';
  direction: 1 | -1;
  /** The one the browser must fetch first. See the img below. */
  first: boolean;
}) {
  const link = useSectorLink();

  // ────────────────── NOTHING PAINTS BEFORE THE ARTWORK ──────────────────
  //
  // Making the first image `eager` and high priority was not enough, and the
  // reason is worth writing down: the tint is a CSS colour and the artwork is
  // a network request, so they can never arrive together.
  //
  // The real order of events is this. The bundle runs, React draws the
  // skeleton, /home is requested, the response comes back, React re-renders
  // with the slides — and at THAT moment the tint paints instantly while the
  // image has not yet been asked for. `eager` only governs what happens after
  // the browser knows the URL, and it cannot know it any sooner than this.
  //
  // So for as long as the download takes, a full-width block of flat colour.
  // On the live shop front that colour is #0A5239, and a dark green rectangle
  // where a banner should be does not read as loading. It reads as broken.
  //
  // The slide now stays transparent until its own image has loaded, so what
  // shows through is the track's neutral `bg-paper` — which looks like the
  // page still filling in, because it is.
  //
  // A slide with NO artwork starts ready: it is text on a colour by design,
  // and waiting for an image that will never arrive would hide it for ever.
  // `onError` counts as ready too, for the same reason — a broken URL must
  // not blank the slide permanently.
  const [ready, setReady] = useState(!slide.image);

  // Every slide is in the DOM and stacked; only the transform and the opacity
  // change. Mounting and unmounting would restart the image load on every turn
  // — which on a slow connection means the carousel flashes grey each time it
  // comes back round to a slide it has already shown.
  const style = transformFor(state, animation, direction);

  const body = (
    <>
      {/* The tint sits BEHIND the artwork and shows through a picture with
          transparency, or around one set to `contain`. It is not meant to be
          looked at on its own, so it does not appear until there is artwork
          for it to sit behind. */}
      <div
        className={`absolute inset-0 -z-10 transition-opacity duration-300 ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: slide.tint }}
      />

      {slide.image ? (
        // <picture>, so the phone downloads the phone artwork and nothing
        // else. The media query matches the `sm:` breakpoint on the box above
        // — they have to agree, or a screen gets one shape's picture in the
        // other shape's box and `cover` crops the difference away.
        //
        // `image_mobile` is optional. Without it the wide file is used at both
        // sizes and cropped on a phone, which is a reasonable default for a
        // slide whose artwork is mostly background.
        <picture>
          {slide.image_mobile ? (
            <source media="(min-width: 640px)" srcSet={slide.image} />
          ) : null}
          <img
            src={slide.image_mobile ?? slide.image}
            // Empty alt and aria-hidden: the headline below is the same
            // information as text, so describing the artwork too makes a
            // screen reader say everything twice.
            alt=""
            aria-hidden
            // ────────────────── THE GREEN FLASH ──────────────────
            //
            // Every slide was `loading="lazy"`, including the first. That one
            // is the biggest thing on the home screen and sits above the fold,
            // so it is the page's largest contentful paint — and `lazy` told
            // the browser to defer exactly the image it should have fetched
            // first.
            //
            // What a buyer saw: the shelf appeared quickly, then a solid block
            // of the slide's tint colour where the artwork should be, then a
            // beat later the real design snapping in. On a slow Nigerian
            // connection that beat is seconds, and a full-width rectangle of
            // flat green reads as a broken image rather than as loading.
            //
            // The first slide is now fetched eagerly and at high priority. The
            // rest stay lazy: they are stacked behind this one and nobody has
            // asked for them yet, so downloading four banners before the shelf
            // would trade one problem for a worse one.
            onLoad={() => setReady(true)}
            // A broken URL must not blank the slide for ever. Whatever the
            // outcome, the slide stops waiting.
            onError={() => setReady(true)}
            loading={first ? 'eager' : 'lazy'}
            fetchPriority={first ? 'high' : 'auto'}
            // `sync` on the first: `async` lets the browser paint the frame
            // WITHOUT the decoded image and fill it in afterwards, which is the
            // flash again in miniature.
            decoding={first ? 'sync' : 'async'}
            className={`absolute inset-0 -z-10 h-full w-full transition-opacity duration-300 ${
              ready ? 'opacity-100' : 'opacity-0'
            } ${
              // `contain` is what makes a DESIGNED banner appear whole: the
              // logo, the headline and the row of icons all survive, where
              // `cover` would crop the top and bottom away.
              slide.fit === 'contain' ? 'object-contain' : 'object-cover'
            } ${animation === 'zoom' && state === 'current' ? 'animate-banner-drift' : ''}`}
          />
        </picture>
      ) : null}

      {/* The scrim, and whether there is one at all.
          
          `auto` is the default and the right one when ChiCare supplies the
          words: without it a white headline disappears the moment somebody
          uploads a picture of a bright laboratory.
          
          But it assumes we are supplying the words. An admin who has had a
          banner DESIGNED — words, logo and all, baked into the artwork — sees
          their own picture looking washed out and dirty, with no way to stop
          it. `none` is for them. */}
      {scrim(slide.overlay) ? (
        <div className="absolute inset-0 -z-10" style={{ background: scrim(slide.overlay)! }} />
      ) : null}

      {/* The type steps DOWN at the wide breakpoint, not up. The strip is
          only 5:1 there, so a 24px headline plus a sub-line plus a button no
          longer fits inside it — and text that overflows a 200px box is how
          a banner starts clipping its own button. */}
      {/* The words wait too. A headline sitting on the page's grey
          background for half a second, before the artwork lands underneath
          it, is its own small flash of wrongness. */}
      <div
        className={`flex h-full flex-col justify-center gap-1 px-4 py-3 transition-opacity duration-300 sm:gap-0.5 sm:px-6 lg:px-8 ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* The shadow is what replaced the scrim.
            
            A gradient across the whole picture to protect these words dimmed
            the three quarters of it with no words anywhere near, and an admin
            who had artwork made saw it through a grey film. A shadow on the
            TEXT survives almost any background and costs the picture nothing.
            
            Three layers, and each earns its place: a 1px near-black edge so
            the letterforms stay crisp, a tight glow for contrast against light
            detail, and a wide soft one that separates the whole block from a
            busy photograph. Nothing touches the image.
            
            On a near-WHITE picture even this is marginal — that is what the
            Light and Strong options in the console are for, and why a slide
            whose words are already in the artwork should simply leave the
            headline empty. */}
        <h3
          className="m-0 max-w-[22ch] font-display text-[15px] font-bold leading-tight text-white sm:max-w-[30ch] sm:text-[17px] lg:text-[21px]"
          style={{ textShadow: '0 1px 1px rgba(0,0,0,.95), 0 2px 10px rgba(0,0,0,.8), 0 0 28px rgba(0,0,0,.5)' }}
        >
          {slide.headline}
        </h3>

        {slide.subline ? (
          <p
            className="m-0 hidden max-w-[40ch] text-[11.5px] leading-snug text-white sm:block lg:text-[13px]"
            style={{ textShadow: '0 1px 1px rgba(0,0,0,.95), 0 1px 8px rgba(0,0,0,.85), 0 0 22px rgba(0,0,0,.5)' }}
          >
            {slide.subline}
          </p>
        ) : null}

        {/* ── A BUTTON ONLY WHERE THERE IS SOMEWHERE TO GO ────────────────
            `cta_label` and `link_path` are separate columns and nothing made
            them agree, so a slide with words on its button and no destination
            drew this: white pill, bold label, a chevron pointing onward — and
            the slide around it is a plain <div>, because the code below
            correctly refuses to wrap a linkless slide in an anchor.

            The result is a control that is unmistakably a button and does
            nothing when pressed. Found on the live shop front, where all four
            slides had `(no link)`.

            The label is kept in the database, not discarded: an admin who adds
            the destination gets their wording back. It simply is not DRAWN as
            a thing to press until there is something to press it for. */}
        {slide.cta_label && slide.link_path ? (
          <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-btn bg-white px-3 py-1.5 text-[11.5px] font-bold text-ink sm:mt-1.5 sm:py-1 sm:text-[12px]">
            {slide.cta_label}
            <Icon name="chevron" size={13} strokeWidth={2.4} />
          </span>
        ) : null}
      </div>
    </>
  );

  const shell = `absolute inset-0 ${state === 'current' ? '' : 'pointer-events-none'}`;

  // A slide with nowhere to go is not a link. Wrapping it in an anchor to
  // nothing gives a screen reader a control that does not work and gives a
  // mouse user a pointer cursor over dead artwork.
  if (!slide.link_path) {
    return (
      <div className={shell} style={style} aria-hidden={state !== 'current'}>
        {body}
      </div>
    );
  }

  return (
    <Link
      /**
       * ── WHY sectorLink AND NOT sectorPath ─────────────────────────────
       *
       * This used to be `sectorPath`, which prefixes /vet unconditionally.
       * The result was that on the animal-health shelf EVERY slide led to a
       * not-found screen:
       *
       *   /verify                        → /vet/verify         (no such route)
       *   /vet/search?category=vaccines  → /vet/vet/search?…   (prefixed twice)
       *
       * The second one is the nastier of the two, because the stored path
       * looked right in the console and reads right in the database.
       *
       * `sectorLink` prefixes only the paths that genuinely have a veterinary
       * twin, so a slide stores the path WITHOUT /vet and the shelf it is
       * drawn on decides. The seeded slides were corrected to match, and
       * `database/scripts/repair-banner-links.php` fixes an install that
       * already has the old ones.
       */
      to={link(slide.link_path)}
      className={`${shell} no-underline`}
      style={style}
      aria-hidden={state !== 'current'}
      tabIndex={state === 'current' ? undefined : -1}
      onClick={() => trackBanners.clicked(slide.id)}
      // The whole slide is draggable, and a browser's native image drag would
      // fight the swipe.
      draggable={false}
    >
      {body}
    </Link>
  );
}

/**
 * The animation, as inline style rather than classes.
 *
 * Because the direction and the state together make more combinations than are
 * worth naming in Tailwind, and because the transition has to be absent on the
 * waiting slides — otherwise a slide that is repositioned off-screen animates
 * its way there and flies across the box.
 */
function transformFor(
  state: SlideState,
  animation: BannerAnimation | 'none',
  direction: 1 | -1,
): React.CSSProperties {
  const ease = 'cubic-bezier(.22,.61,.36,1)';

  if (animation === 'none') {
    // No transition at all, not a fast one. "Reduced motion" means none.
    return { opacity: state === 'current' ? 1 : 0, visibility: state === 'current' ? 'visible' : 'hidden' };
  }

  const resting: React.CSSProperties = {
    transition: `transform 620ms ${ease}, opacity 480ms ease`,
    opacity: 1,
    transform: 'none',
    zIndex: 2,
  };

  if (state === 'current') {
    return animation === 'zoom' ? { ...resting, transform: 'scale(1)' } : resting;
  }

  // Where a slide sits when it is not the one showing. `leaving` goes the
  // opposite way to `waiting` so the two cross, which is what makes a slide
  // look like it was pushed rather than swapped.
  const away = state === 'leaving' ? -direction : direction;

  const hidden: React.CSSProperties = {
    transition: state === 'leaving' ? `transform 620ms ${ease}, opacity 480ms ease` : 'none',
    opacity: 0,
    zIndex: 1,
  };

  switch (animation) {
    case 'fade':
      return hidden;
    case 'zoom':
      return { ...hidden, transform: `scale(${state === 'leaving' ? 1.06 : 0.94})`, opacity: 0 };
    case 'flip':
      return {
        ...hidden,
        transform: `perspective(1200px) rotateY(${away * 42}deg) scale(.92)`,
        transformOrigin: away > 0 ? 'left center' : 'right center',
      };
    case 'slide':
    default:
      return { ...hidden, transform: `translateX(${away * 100}%)`, opacity: 1 };
  }
}

function Arrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      // Hidden from screen readers: the dots already expose every slide by
      // name, and a reader does not benefit from "next" and "previous" on top.
      aria-hidden
      tabIndex={-1}
      onClick={onClick}
      className={`absolute top-1/2 hidden h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink shadow-sm transition hover:bg-white sm:grid ${
        side === 'left' ? 'left-2' : 'right-2'
      }`}
    >
      <Icon name={side === 'left' ? 'back' : 'chevron'} size={16} strokeWidth={2.4} />
    </button>
  );
}
